export const tooltipData = {
  // Sales Value
  merchandiseRevenue: {
    title: 'Merchandise Revenue',
    definition: 'Direct sales attributed to sponsorship activation',
    assessmentCriteria: [
      'Branded merchandise with sponsor logos',
      'Limited edition sponsor-related products',
      'Online and physical store sales tracking',
      'Direct financial tracking',
    ],
    impact: 'Data Quality: HIGH - Direct financial tracking with clear attribution (95%+ accuracy)',
  },

  matchdayRevenue: {
    title: 'Matchday Revenue',
    definition: 'Revenue generated on match days attributed to sponsor activities',
    assessmentCriteria: [
      'Increased ticket sales due to sponsor events',
      'Premium pricing for sponsor-activated matches',
      'Concession and hospitality revenue increases',
      'Parking and ancillary service revenue',
    ],
    impact: 'Data Quality: HIGH - Direct financial tracking with clear attribution (95%+ accuracy)',
  },

  playerSales: {
    title: 'Player Sales Value',
    definition: 'Transfer value enhancement through exposure during sponsorship period',
    assessmentCriteria: [
      'Transfer value enhancement through exposure',
      'Market value increases during sponsorship period',
      'Performance bonuses triggered by sponsor metrics',
      'Image rights value appreciation',
    ],
    impact: 'Data Quality: MEDIUM - Industry estimates and attribution (80-95% accuracy)',
  },

  // Media Value - TV
  tvDuration: {
    title: 'TV Duration',
    definition: 'Exact logo visibility time in minutes on television broadcast',
    assessmentCriteria: [
      'Measure exact duration of logo visibility',
      'Include all on-screen appearances',
      'Use official broadcast measurement tools',
    ],
    impact: 'Data Quality: HIGH - Direct measurement from broadcast data (95%+ accuracy)',
  },

  tvSize: {
    title: 'TV Size Multiplier',
    definition: 'Logo size category on screen as percentage of total screen area',
    scoringGuidelines: [
      { range: 'S (Small)', description: '≤1% of screen area (0.2x multiplier)' },
      { range: 'M (Medium)', description: '~1-5% of screen area (0.7x multiplier)' },
      { range: 'L (Large)', description: '~5-15% of screen area (1.0x multiplier)' },
      { range: 'XL (Extra Large)', description: '>15% of screen area (1.5x multiplier)' },
    ],
    impact: 'Larger logo sizes increase visibility and brand recall',
  },

  tvCpm: {
    title: 'TV CPM (Cost Per Thousand)',
    definition: 'Cost per thousand viewers for TV advertising in your market',
    assessmentCriteria: [
      'Market rates for TV advertising',
      'Time slot and program type',
      'Audience demographics',
      'Geographic market size',
    ],
    impact: 'Benchmark range: €15-75 depending on market and program quality',
  },

  tvViewers: {
    title: 'TV Viewers',
    definition: 'Official audience figures for the broadcast',
    assessmentCriteria: [
      'Official ratings from Nielsen, BARB, AGF, Médiamétrie, or Auditel',
      'Live viewers plus catch-up within 7 days',
      'Verified audience measurement data',
    ],
    impact: 'Data Quality: HIGH - Official measurement from recognized agencies (95%+ accuracy)',
  },

  // Media Value - Web TV
  webtvViews: {
    title: 'Web TV Views',
    definition: 'Total video views with logo visibility on streaming platforms',
    assessmentCriteria: [
      'YouTube Analytics and platform APIs',
      'Total views of videos showing sponsor logo',
      'Include all streaming platforms',
    ],
    impact: 'Data Quality: HIGH - Platform analytics provide accurate view counts (95%+ accuracy)',
  },

  webtvCpm: {
    title: 'Web TV CPM',
    definition: 'Digital video advertising rates for your content type',
    assessmentCriteria: [
      'Digital video advertising market rates',
      'Platform type (YouTube, Twitch, etc.)',
      'Content category and audience demographics',
    ],
    scoringGuidelines: [
      { range: '€3-6', description: 'Low' },
      { range: '€6-10', description: 'Medium' },
      { range: '€10-15', description: 'High' },
      { range: '€15-25', description: 'Premium' },
    ],
  },

  // Media Value - PR
  prReach: {
    title: 'PR Reach',
    definition: 'Total audience reached through PR mentions and earned media',
    assessmentCriteria: [
      'Media monitoring tools (Meltwater, Brandwatch, Mention)',
      'Total readership/viewership of publications',
      'Social media reach of PR coverage',
      'Google Alerts and news aggregation',
    ],
    impact: 'Data Quality: MEDIUM - Third-party estimates (80-95% accuracy)',
  },

  prCpm: {
    title: 'PR CPM',
    definition: 'Public relations equivalent advertising value rate',
    assessmentCriteria: [
      'PR equivalent advertising value',
      'Publication quality and reach',
      'Editorial vs. advertorial weighting',
    ],
    scoringGuidelines: [
      { range: '€2-4', description: 'Low' },
      { range: '€4-7', description: 'Medium' },
      { range: '€7-12', description: 'High' },
      { range: '€12-20', description: 'Premium' },
    ],
  },

  // Media Value - Digital
  digitalImpressions: {
    title: 'Digital Impressions',
    definition: 'Total digital ad impressions showing sponsor logo',
    assessmentCriteria: [
      'Google Analytics and Adobe Analytics data',
      'Display advertising impressions',
      'Banner and native ad placements',
      'Platform dashboard metrics',
    ],
    impact: 'Data Quality: HIGH - Direct platform measurement (95%+ accuracy)',
  },

  digitalCpm: {
    title: 'Digital CPM',
    definition: 'Digital advertising market rates for display ads',
    assessmentCriteria: [
      'Digital advertising market rates',
      'Ad placement type and format',
      'Target audience demographics',
      'Geographic targeting',
    ],
    scoringGuidelines: [
      { range: '€1-3', description: 'Low' },
      { range: '€3-6', description: 'Medium' },
      { range: '€6-10', description: 'High' },
      { range: '€10-15', description: 'Premium' },
    ],
  },

  // Media Value - Social
  socialFollowers: {
    title: 'Social Media Followers',
    definition: 'Total number of followers across all social media platforms',
    assessmentCriteria: [
      'Instagram, Facebook, Twitter/X, TikTok, LinkedIn follower counts',
      'Native platform analytics',
      'Sum of all platform followers',
      'Verified follower metrics',
    ],
    impact: 'Data Quality: HIGH - Platform analytics provide accurate counts (95%+ accuracy)',
  },

  socialFollowersCpm: {
    title: 'Social Media Followers CPM',
    definition: 'Cost per thousand followers for social media advertising',
    assessmentCriteria: [
      'Platform-specific follower acquisition costs',
      'Influencer marketing rates',
      'Target audience demographics',
      'Platform and content type',
    ],
    scoringGuidelines: [
      { range: '€1-3', description: 'Low' },
      { range: '€3-5', description: 'Medium' },
      { range: '€5-8', description: 'High' },
      { range: '€8-12', description: 'Premium' },
    ],
  },

  socialImpressions: {
    title: 'Social Media Impressions',
    definition: 'Total number of times social media content was displayed',
    assessmentCriteria: [
      'Post impressions across all platforms',
      'Native platform analytics (Instagram Insights, etc.)',
      'Organic and paid impressions combined',
      'Verified impression metrics',
    ],
    impact: 'Data Quality: HIGH - Platform analytics provide accurate counts (95%+ accuracy)',
  },

  socialImpressionsCpm: {
    title: 'Social Media Impressions CPM',
    definition: 'Cost per thousand impressions for social media advertising',
    assessmentCriteria: [
      'Platform-specific advertising rates',
      'Content format (feed, stories, reels)',
      'Target audience demographics',
      'Campaign objectives',
    ],
    scoringGuidelines: [
      { range: '€2-4', description: 'Low' },
      { range: '€4-7', description: 'Medium' },
      { range: '€7-12', description: 'High' },
      { range: '€12-20', description: 'Premium' },
    ],
  },

  socialEngagements: {
    title: 'Social Media Engagements',
    definition: 'Total social media interactions with sponsor-branded content',
    assessmentCriteria: [
      'Likes, comments, shares, and saves',
      'Native platform analytics (Instagram Insights, etc.)',
      'All social media platforms combined',
      'Verified engagement metrics',
    ],
    impact: 'Data Quality: HIGH - Platform analytics provide accurate counts (95%+ accuracy)',
  },

  socialCpm: {
    title: 'Social Media Engagements CPM',
    definition: 'Cost per thousand engagements for social media advertising',
    assessmentCriteria: [
      'Platform-specific advertising rates',
      'Content type and format',
      'Audience targeting parameters',
      'Engagement quality',
    ],
    scoringGuidelines: [
      { range: '€3-6', description: 'Low' },
      { range: '€6-10', description: 'Medium' },
      { range: '€10-15', description: 'High' },
      { range: '€15-25', description: 'Premium' },
    ],
  },

  // Media Value - Influencers
  influencerReach: {
    title: 'Influencer Reach',
    definition: 'Total audience reach from influencer and creator collaborations',
    assessmentCriteria: [
      'Follower counts of collaborating influencers/creators',
      'Native platform analytics from influencer accounts',
      'Sum across all influencer collaborations',
      'Verified reach metrics',
    ],
    impact: 'Data Quality: HIGH - Platform analytics provide accurate counts (95%+ accuracy)',
  },

  influencerReachCpm: {
    title: 'Influencer Reach CPM',
    definition: 'Cost per thousand reached via influencer marketing',
    assessmentCriteria: [
      'Influencer marketing market rates',
      'Creator tier and niche',
      'Audience quality and demographics',
      'Platform and content type',
    ],
    scoringGuidelines: [
      { range: '€1-3', description: 'Low' },
      { range: '€3-5', description: 'Medium' },
      { range: '€5-10', description: 'High' },
      { range: '€10-20', description: 'Premium' },
    ],
  },

  influencerImpressions: {
    title: 'Influencer Impressions',
    definition: 'Total impressions generated by influencer and creator content',
    assessmentCriteria: [
      'Native platform analytics from influencer posts',
      'Story, post, and video impressions combined',
      'All collaborating influencers combined',
      'Verified impression metrics',
    ],
    impact: 'Data Quality: HIGH - Platform analytics provide accurate counts (95%+ accuracy)',
  },

  influencerImpressionsCpm: {
    title: 'Influencer Impressions CPM',
    definition: 'Cost per thousand impressions for influencer content',
    assessmentCriteria: [
      'Influencer advertising rates',
      'Content format and placement',
      'Audience targeting parameters',
      'Platform-specific benchmarks',
    ],
    scoringGuidelines: [
      { range: '€2-4', description: 'Low' },
      { range: '€4-7', description: 'Medium' },
      { range: '€7-12', description: 'High' },
      { range: '€12-20', description: 'Premium' },
    ],
  },

  influencerEngagements: {
    title: 'Influencer Engagements',
    definition: 'Total interactions with influencer and creator content',
    assessmentCriteria: [
      'Likes, comments, shares, and saves',
      'Native platform analytics from influencer accounts',
      'All collaborating influencers combined',
      'Verified engagement metrics',
    ],
    impact: 'Data Quality: HIGH - Platform analytics provide accurate counts (95%+ accuracy)',
  },

  influencerEngagementsCpm: {
    title: 'Influencer Engagements CPM',
    definition: 'Cost per thousand engagements for influencer content',
    assessmentCriteria: [
      'Influencer engagement rates',
      'Content type and format',
      'Audience quality',
      'Engagement depth (comments vs. likes)',
    ],
    scoringGuidelines: [
      { range: '€3-6', description: 'Low' },
      { range: '€6-10', description: 'Medium' },
      { range: '€10-15', description: 'High' },
      { range: '€15-25', description: 'Premium' },
    ],
  },

  // Media Value - Match Attendance
  attendance: {
    title: 'Match Attendance',
    definition: 'Official attendance figures for events with sponsor visibility',
    assessmentCriteria: [
      'Official attendance records',
      'Venue capacity data',
      'Ticket sales verification',
      'In-person brand exposure',
    ],
    impact: 'Data Quality: HIGH - Official venue records (95%+ accuracy)',
  },

  attendanceCpm: {
    title: 'Attendance CPM',
    definition: 'Event marketing equivalent rates for in-person exposure',
    assessmentCriteria: [
      'Event marketing equivalent rates',
      'Venue type and size',
      'Event category and prestige',
      'Dwell time and exposure quality',
    ],
    scoringGuidelines: [
      { range: '€5-10', description: 'Low' },
      { range: '€10-15', description: 'Medium' },
      { range: '€15-25', description: 'High' },
      { range: '€25-40', description: 'Premium' },
    ],
  },

  // Media Value - Hospitality
  hospitalityGuests: {
    title: 'Hospitality Guests',
    definition: 'Number of hospitality attendees with premium sponsor exposure',
    assessmentCriteria: [
      'Event management systems data',
      'Catering records',
      'VIP and hospitality area attendance',
      'High-value audience engagement',
    ],
    impact: 'Data Quality: HIGH - Direct tracking from booking systems (95%+ accuracy)',
  },

  hospitalityValue: {
    title: 'Hospitality',
    definition: 'Market rate for equivalent hospitality experiences',
    assessmentCriteria: [
      'Market rate for equivalent experiences',
      'Premium seating and amenities',
      'Food and beverage quality',
      'Exclusive access and networking value',
    ],
    scoringGuidelines: [
      { range: '€100-300', description: 'Low' },
      { range: '€300-600', description: 'Medium' },
      { range: '€600-1000', description: 'High' },
      { range: '€1000-2000', description: 'Premium' },
    ],
  },

  // Brand Value Indicators - Popularity
  popularity: {
    title: 'Popularity Assessment',
    definition: 'General public awareness and appeal of the sponsored entity',
    assessmentCriteria: [
      'Media coverage frequency and tone',
      'Social media following and engagement rates',
      'Public recognition and brand awareness surveys',
      'Celebrity status and mainstream appeal',
      'Search volume and online interest trends',
    ],
    scoringGuidelines: [
      { range: '1-2', description: 'Limited public awareness, niche appeal only' },
      { range: '3-4', description: 'Regional recognition, moderate media coverage' },
      { range: '5-6', description: 'National awareness, regular media presence' },
      { range: '7-8', description: 'High public profile, significant media attention' },
      { range: '9-10', description: 'Global recognition, celebrity status, viral appeal' },
    ],
    impact: 'Higher popularity increases brand reach and message amplification',
    wide: true,
  },

  // Brand Value Indicators - CSR
  csr: {
    title: 'CSR (Corporate Social Responsibility) Assessment',
    definition: 'Alignment with corporate social responsibility and positive social impact',
    assessmentCriteria: [
      'Environmental sustainability initiatives',
      'Community engagement and charitable activities',
      'Diversity and inclusion efforts',
      'Ethical business practices and transparency',
      'Social cause advocacy and awareness campaigns',
    ],
    scoringGuidelines: [
      { range: '1-2', description: 'No visible CSR activities, potential negative associations' },
      { range: '3-4', description: 'Minimal CSR efforts, basic compliance only' },
      { range: '5-6', description: 'Moderate CSR activities, some community engagement' },
      { range: '7-8', description: 'Strong CSR program, recognized social impact' },
      { range: '9-10', description: 'Industry-leading CSR, significant positive social change' },
    ],
    impact: 'Strong CSR enhances brand reputation and stakeholder approval',
    wide: true,
  },

  // Brand Value Indicators - Culture
  culture: {
    title: 'Culture Assessment',
    definition: 'Cultural fit and relevance to target audience and market context',
    assessmentCriteria: [
      'Alignment with local cultural values and traditions',
      'Relevance to target demographic preferences',
      'Cultural sensitivity and appropriate messaging',
      'Integration with cultural events and celebrations',
      'Respect for cultural diversity and inclusion',
    ],
    scoringGuidelines: [
      { range: '1-2', description: 'Cultural misalignment, potential offense or irrelevance' },
      { range: '3-4', description: 'Limited cultural connection, generic appeal' },
      { range: '5-6', description: 'Moderate cultural fit, some local relevance' },
      { range: '7-8', description: 'Strong cultural alignment, resonates with audience' },
      {
        range: '9-10',
        description: 'Perfect cultural integration, becomes part of cultural fabric',
      },
    ],
    impact: 'Cultural alignment increases audience connection and message effectiveness',
    wide: true,
  },

  // Brand Value Indicators - Fans/Loyalty
  fansLoyalty: {
    title: 'Fans/Loyalty Assessment',
    definition: 'Fan base engagement level and loyalty strength',
    assessmentCriteria: [
      'Fan base size and growth trends',
      'Engagement quality and emotional connection',
      'Repeat attendance and purchase behavior',
      'Fan community activity and advocacy',
      'Resistance to competitive offerings',
    ],
    scoringGuidelines: [
      { range: '1-2', description: 'Weak fan base, low engagement, high churn' },
      { range: '3-4', description: 'Casual following, moderate engagement' },
      { range: '5-6', description: 'Solid fan base, regular engagement' },
      { range: '7-8', description: 'Strong loyal following, high engagement' },
      { range: '9-10', description: 'Passionate fan base, exceptional loyalty, advocacy' },
    ],
    impact: 'Strong fan loyalty ensures sustained value and message amplification',
    wide: true,
  },
};
